import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { 
  BookOpen, 
  ExternalLink, 
  ChevronLeft, 
  Barcode, 
  Building2, 
  ShieldCheck, 
  ShoppingBag,
  CheckCircle2,
  Globe2
} from 'lucide-react';

import { Breadcrumb } from '../../components/common/Breadcrumb';

export const PublicationDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: book, isLoading, error } = useQuery({
    queryKey: ['public-publication-detail', slug],
    queryFn: () => api.get(`/public/publications/${slug}`).then((res) => res.data.data),
    enabled: !!slug,
  });

  if (isLoading) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-xs font-mono">Loading publication record...</div>;
  }

  if (error || !book) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center space-y-4">
        <h2 className="text-2xl font-serif font-bold text-slate-900">Publication Record Not Found</h2>
        <Link to="/publications" className="text-xs font-bold text-brand-700 hover:underline inline-flex items-center gap-1">
          <ChevronLeft className="w-4 h-4" /> Back to Production Archive
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8 py-8 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 font-sans antialiased text-slate-900">
      
      {/* Breadcrumbs */}
      <div>
        <Breadcrumb
          items={[
            { label: 'Production Archive', href: '/publications' },
            { label: book.title },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        
        {/* Cover image & Store Order column */}
        <div className="md:col-span-5 space-y-5">
          <div className="aspect-4/5 rounded-2xl overflow-hidden shadow-book border border-slate-200 bg-slate-50 flex items-center justify-center p-6">
            <img src={book.coverImage} alt={book.title} className="max-h-full max-w-full object-contain drop-shadow-xl" />
          </div>

          {/* Bookstore Purchase Action */}
          {book.bookstoreUrl && (
            <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200/90 space-y-2.5">
              <div className="flex items-center gap-1.5 text-amber-900 font-bold text-xs uppercase tracking-wider">
                <ShoppingBag className="w-3.5 h-3.5 text-amber-700" />
                <span>Commercial Order &amp; Retail Purchase</span>
              </div>
              <p className="text-[11px] text-amber-800 leading-relaxed font-normal">
                Direct retail customer orders and doorstep delivery across India are fulfilled via our commercial storefront at <strong>forthetruth.in</strong>.
              </p>
              <a
                href={book.bookstoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs shadow-xs transition-colors"
              >
                <span>Order on forthetruth.in</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          {/* Corporate Rights Attestation */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 space-y-1.5">
            <div className="flex items-center gap-1.5 text-brand-800 font-bold text-[11px] uppercase tracking-wider font-mono">
              <Building2 className="w-3.5 h-3.5 text-brand-600" />
              <span>FOR THE TRUTH PRESS RECORD</span>
            </div>
            <p className="text-[11px] leading-relaxed">
              Official catalog entry for trade distribution, rights administration, and statutory legal deposit under FOR THE TRUTH PRESS PRIVATE LIMITED.
            </p>
          </div>
        </div>

        {/* Technical Details column */}
        <div className="md:col-span-7 space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono uppercase tracking-widest text-brand-700 font-bold">
                {book.category}
              </span>
              <span className="text-slate-300">&bull;</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 font-bold border border-emerald-200">
                Official FTT Edition
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif font-black text-slate-900 leading-tight">
              {book.title}
            </h1>
            <p className="text-base text-slate-600 font-medium">
              By <span className="font-bold text-slate-900">{book.author}</span>
            </p>
          </div>

          {/* Technical Metadata Grid */}
          <div className="grid grid-cols-2 gap-3 p-5 rounded-2xl bg-white border border-slate-200/90 shadow-2xs text-xs font-mono">
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Physical Format</span>
              <span className="font-bold text-slate-900">{book.format}</span>
            </div>
            <div>
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Imprint / Partner</span>
              <span className="font-bold text-slate-900">{book.publisher || 'FOR THE TRUTH'}</span>
            </div>
            {book.isbn && (
              <div className="col-span-2 pt-2 border-t border-slate-100">
                <span className="text-slate-400 block text-[10px] uppercase font-semibold">International Standard Book Number (ISBN)</span>
                <div className="flex items-center gap-2 pt-0.5">
                  <span className="font-bold text-slate-900 text-sm">{book.isbn}</span>
                  <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-sans font-bold flex items-center gap-1 border border-slate-200">
                    <ShieldCheck className="w-3 h-3 text-emerald-600" />
                    <span>Registered via isbn.gov.in</span>
                  </span>
                </div>
              </div>
            )}
            <div className="pt-2 border-t border-slate-100">
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Publication Year</span>
              <span className="font-bold text-slate-900">
                {new Date(book.publicationDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
              </span>
            </div>
            <div className="pt-2 border-t border-slate-100">
              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Territory Rights</span>
              <span className="font-bold text-slate-900">India &amp; South Asia</span>
            </div>
          </div>

          {/* Synopsis */}
          <div className="space-y-2">
            <h3 className="font-serif text-lg font-bold text-slate-900">About this Volume</h3>
            <p className="text-sm text-slate-600 leading-relaxed font-sans font-medium">
              {book.description}
            </p>
          </div>

          {/* Excerpt */}
          {book.excerpt && (
            <div className="p-5 rounded-2xl bg-slate-50 border-l-4 border-brand-500 space-y-1.5">
              <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-800 block">
                Selected Excerpt
              </span>
              <blockquote className="font-serif italic text-sm text-slate-700 leading-relaxed">
                &ldquo;{book.excerpt}&rdquo;
              </blockquote>
            </div>
          )}

          {/* Institutional / Rights Inquiry Link */}
          <div className="pt-3 border-t border-slate-200 flex items-center justify-between text-xs">
            <Link to="/publications" className="text-slate-600 hover:text-slate-900 font-bold flex items-center gap-1">
              <ChevronLeft className="w-3.5 h-3.5" />
              <span>Back to Production Archive</span>
            </Link>
            <Link to="/contact" className="text-brand-700 hover:text-brand-900 font-bold">
              Institutional / Bulk Rights Enquiry &rarr;
            </Link>
          </div>

        </div>
      </div>

    </div>
  );
};
